import { lisbonDate, sessionRowSchema, type SessionRow } from '@growup/arena-shared';
import { Button, Card, DateInput, Field, Notice, Table } from '@growup/ui';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { download, getJson, messageOf } from '../api';
import { formatDateTime, formatMinutes } from '../format';

const REASON: Record<string, string> = {
  USER_ENDED: 'Terminou',
  WORKSTATION_LOCKED: 'Bloqueou o Windows',
  SHUTDOWN: 'PC desligado',
  IDLE_TIMEOUT: 'Inativo',
  HEARTBEAT_LOST: 'Ligação perdida',
  ADMIN_CLOSED: 'Fechada pelo monitor',
};

/** Sessions of a period: what the Excel sheet used to hold. */
export function SessionsView({ arenaId }: { arenaId: string }) {
  const today = lisbonDate(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const query = `from=${from}&to=${to}`;

  useEffect(() => {
    getJson(`/api/arena/arenas/${arenaId}/sessions?${query}`, z.array(sessionRowSchema))
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [arenaId, query]);

  const total = rows.filter((r) => !r.isEvent).reduce((sum, r) => sum + r.minutes, 0);

  return (
    <Card
      headingLevel={2}
      title="Sessões"
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            void download(
              `/api/arena/arenas/${arenaId}/sessions.csv?${query}`,
              `sessoes-${from}-${to}.csv`,
            ).catch((e: unknown) => setError(messageOf(e)))
          }
        >
          Exportar CSV
        </Button>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <Field label="De">
          <DateInput value={from} onChange={setFrom} max={to || undefined} />
        </Field>
        <Field label="Até">
          <DateInput value={to} onChange={setTo} min={from || undefined} />
        </Field>
        <p className="pb-3 text-body text-gu-ink-muted">
          {rows.length} sessões · {formatMinutes(total)} de jogo
        </p>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Table
        caption="Sessões do período"
        rowKey={(r) => r.id}
        rows={rows}
        empty="Sem sessões neste período."
        columns={[
          { key: 'pc', header: 'Posto', cell: (r) => r.workstationName },
          {
            key: 'login',
            header: 'Login',
            cell: (r) => (
              <>
                {r.isEvent ? <em className="text-gu-ink-muted">evento</em> : r.login}
                {r.duringBreak ? (
                  <span className="ml-1 text-gu-info" title="Entrou durante a pausa">
                    <span aria-hidden="true">⏸</span>
                    <span className="sr-only">entrou durante a pausa</span>
                  </span>
                ) : null}
              </>
            ),
          },
          { key: 'in', header: 'Entrada', cell: (r) => formatDateTime(r.startedAt) },
          {
            key: 'out',
            header: 'Saída',
            cell: (r) => (r.endedAt ? formatDateTime(r.endedAt) : 'a decorrer'),
          },
          { key: 'time', header: 'Tempo', align: 'right', cell: (r) => formatMinutes(r.minutes) },
          {
            key: 'end',
            header: 'Fim',
            hideOnMobile: true,
            cell: (r) => (r.endReason ? (REASON[r.endReason] ?? r.endReason) : ''),
          },
        ]}
      />
    </Card>
  );
}
